'use client';

import React, { useState } from 'react';
import { kavrisNodes, atlasLayers, KavrisNode, NodeStatus } from '@/data/kavrisData';
import StatusBadge from '@/components/ui/StatusBadge';
import AuthorityBadge from '@/components/ui/AuthorityBadge';
import DemoBadge from '@/components/ui/DemoBadge';
import { Layers, Network, LayoutGrid, X, ChevronRight, Info } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


type AtlasView = 'ecosystem' | 'layer' | 'relationship';

export default function AtlasWorkspace() {
  const [activeView, setActiveView] = useState<AtlasView>('ecosystem');
  const [selectedNode, setSelectedNode] = useState<KavrisNode | null>(null);

  const views: { id: AtlasView; label: string; icon: React.ElementType }[] = [
    { id: 'ecosystem', label: 'Ecosystem', icon: LayoutGrid },
    { id: 'layer', label: 'Layer', icon: Layers },
    { id: 'relationship', label: 'Relationship', icon: Network },
  ];

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 'calc(100vh - 56px)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-background/60 backdrop-blur-sm flex-shrink-0">
        <div>
          <div className="text-xs font-mono-data text-muted-foreground mb-1">KAVRIS → Atlas</div>
          <h1 className="text-lg font-semibold text-foreground">Atlas</h1>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg border border-border bg-muted/20">
          {views.map(v => {
            const Icon = v.icon;
            return (
              <button
                key={`view-${v.id}`}
                onClick={() => setActiveView(v.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-all duration-150 ${
                  activeView === v.id
                    ? 'bg-primary/15 text-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon size={13} />
                <span className="hidden sm:block">{v.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* View area */}
        <div className="flex-1 overflow-y-auto p-6 min-w-0">
          <DemoBadge label="PROTOTYPE — Atlas visualization" />
          <div className="mt-4">
            {activeView === 'ecosystem' && (
              <EcosystemView selectedNode={selectedNode} onSelectNode={setSelectedNode} />
            )}
            {activeView === 'layer' && (
              <LayerView selectedNode={selectedNode} onSelectNode={setSelectedNode} />
            )}
            {activeView === 'relationship' && (
              <RelationshipView selectedNode={selectedNode} onSelectNode={setSelectedNode} />
            )}
          </div>
        </div>

        {/* Detail panel */}
        {selectedNode && (
          <AtlasDetailPanel node={selectedNode} onClose={() => setSelectedNode(null)} />
        )}
      </div>
    </div>
  );
}

function EcosystemView({
  selectedNode,
  onSelectNode,
}: {
  selectedNode: KavrisNode | null;
  onSelectNode: (n: KavrisNode) => void;
}) {
  const nonCore = kavrisNodes.filter(n => n.id !== 'node-kavris-core');
  const core = kavrisNodes.find(n => n.id === 'node-kavris-core');

  return (
    <div>
      <h2 className="text-sm font-semibold text-foreground mb-1">Ecosystem View</h2>
      <p className="text-xs text-muted-foreground mb-6">High-level architecture of the KAVRIS ecosystem.</p>

      {/* Core node */}
      {core && (
        <div className="flex flex-col items-center mb-8">
          <button
            onClick={() => onSelectNode(core)}
            className={`px-8 py-4 rounded-xl border transition-all duration-200 active:scale-[0.98] ${
              selectedNode?.id === core.id
                ? 'border-primary/60 bg-primary/10 node-glow-active' :'border-primary/30 bg-primary/5 node-glow hover:border-primary/50'
            }`}
          >
            <div className="text-lg font-bold tracking-[0.1em] text-primary uppercase mb-1">KAVRIS</div>
            <div className="text-xs text-muted-foreground font-mono-data">{core.type}</div>
            <div className="flex items-center justify-center gap-2 mt-2">
              <StatusBadge status={core.status} size="xs" />
              <span className="text-[10px] font-mono-data text-muted-foreground">{core.version}</span>
            </div>
          </button>
          <div className="w-px h-8 bg-gradient-to-b from-primary/40 to-transparent mt-2" />
        </div>
      )}

      {/* Child nodes grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {nonCore.map(node => (
          <button
            key={`eco-${node.id}`}
            onClick={() => onSelectNode(node)}
            className={`text-left p-4 rounded-xl border transition-all duration-200 active:scale-[0.98] ${
              selectedNode?.id === node.id
                ? 'border-primary/50 bg-primary/8 node-glow' :'border-border bg-card hover:border-border/80 hover:bg-muted/30'
            }`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="text-sm font-semibold text-foreground">{node.label}</span>
              <StatusBadge status={node.status} size="xs" />
            </div>
            <div className="text-[10px] font-mono-data text-muted-foreground mb-2 uppercase tracking-wide">{node.type}</div>
            <p className="text-xs text-muted-foreground leading-relaxed">{node.shortDescription}</p>
            <div className="flex items-center gap-2 mt-3">
              <AuthorityBadge authority={node.authority} size="xs" />
              <span className="text-[10px] font-mono-data text-muted-foreground">{node.layer} layer</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function LayerView({
  selectedNode,
  onSelectNode,
}: {
  selectedNode: KavrisNode | null;
  onSelectNode: (n: KavrisNode) => void;
}) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-foreground mb-1">Layer View</h2>
      <p className="text-xs text-muted-foreground mb-2">
        Conceptual layers of the KAVRIS architecture — prototype naming, not approved taxonomy.
      </p>
      <div className="flex items-center gap-2 mb-6">
        <Info size={12} className="text-[#a855f7]" />
        <span className="text-[11px] text-[#a855f7] font-mono-data">Layer labels are experimental and subject to change.</span>
      </div>

      <div className="flex flex-col gap-2 max-w-2xl">
        {atlasLayers.map((layer, i) => {
          const layerNodes = kavrisNodes.filter(n => layer.nodeIds.includes(n.id));
          const depthPercent = (i / (atlasLayers.length - 1)) * 100;
          return (
            <div
              key={layer.id}
              className="relative flex items-stretch rounded-xl border border-border overflow-hidden"
              style={{ paddingLeft: `${i * 16}px` }}
            >
              {/* Depth indicator bar */}
              <div
                className="w-1 flex-shrink-0"
                style={{
                  background: `linear-gradient(180deg, rgba(79,142,247,${0.6 - i * 0.08}) 0%, rgba(79,142,247,${0.2 - i * 0.02}) 100%)`,
                }}
              />
              <div className="flex-1 p-4 bg-card hover:bg-muted/20 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-mono-data text-muted-foreground">L{i + 1}</span>
                      <span className="text-sm font-semibold text-foreground">{layer.label}</span>
                      <StatusBadge status={layer.status as NodeStatus} size="xs" />
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{layer.description}</p>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {layerNodes.map(node => (
                      <button
                        key={`layer-node-${node.id}`}
                        onClick={() => onSelectNode(node)}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${
                          selectedNode?.id === node.id
                            ? 'border-primary/50 bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                        }`}
                      >
                        {node.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RelationshipView({
  selectedNode,
  onSelectNode,
}: {
  selectedNode: KavrisNode | null;
  onSelectNode: (n: KavrisNode) => void;
}) {
  const displayNode = selectedNode || kavrisNodes.find(n => n.id === 'node-kavris-core')!;
  const related = kavrisNodes.filter(n => displayNode.relatedIds.includes(n.id));

  return (
    <div>
      <h2 className="text-sm font-semibold text-foreground mb-1">Relationship View</h2>
      <p className="text-xs text-muted-foreground mb-6">
        Select any node below to explore its relationships.
      </p>

      {/* Node selector */}
      <div className="flex flex-wrap gap-2 mb-8">
        {kavrisNodes.map(node => (
          <button
            key={`rel-select-${node.id}`}
            onClick={() => onSelectNode(node)}
            className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
              selectedNode?.id === node.id || (!selectedNode && node.id === 'node-kavris-core')
                ? 'border-primary/50 bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
            }`}
          >
            {node.label}
          </button>
        ))}
      </div>

      {/* Relationship display */}
      <div className="max-w-2xl">
        {/* Focus node */}
        <div className="p-4 rounded-xl border border-primary/40 bg-primary/5 mb-4">
          <div className="text-[10px] font-mono-data text-muted-foreground uppercase tracking-widest mb-1">Selected Node</div>
          <div className="text-base font-semibold text-foreground mb-1">{displayNode.label}</div>
          <p className="text-xs text-muted-foreground">{displayNode.shortDescription}</p>
          <div className="flex items-center gap-2 mt-3">
            <StatusBadge status={displayNode.status} size="xs" />
            <AuthorityBadge authority={displayNode.authority} size="xs" />
          </div>
        </div>

        {/* Relationships */}
        <div className="text-[10px] font-mono-data text-muted-foreground uppercase tracking-widest mb-3">
          Connected Nodes ({related.length})
        </div>
        <div className="flex flex-col gap-2">
          {related.map(rel => (
            <button
              key={`rel-${rel.id}`}
              onClick={() => onSelectNode(rel)}
              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/30 transition-all text-left active:scale-[0.99]"
            >
              <div className="flex flex-col items-center gap-1 w-6">
                <div className="w-px h-2 bg-border" />
                <span className="text-[10px] text-primary/60">↔</span>
                <div className="w-px h-2 bg-border" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">{rel.label}</div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">{rel.shortDescription}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <StatusBadge status={rel.status} size="xs" />
                <ChevronRight size={12} className="text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AtlasDetailPanel({ node, onClose }: { node: KavrisNode; onClose: () => void }) {
  const parent = node.parentId ? kavrisNodes.find(n => n.id === node.parentId) : null;
  const related = kavrisNodes.filter(n => node.relatedIds.includes(n.id));

  return (
    <div className="w-80 xl:w-96 border-l border-border bg-card flex-shrink-0 overflow-y-auto panel-slide-in">
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-4">
          <div>
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-1">
              {node.type}
            </div>
            <h3 className="text-base font-semibold text-foreground">{node.label}</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors flex-shrink-0">
            <X size={14} />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4">
          <StatusBadge status={node.status} size="sm" />
          <AuthorityBadge authority={node.authority} size="sm" />
          <span className="text-[10px] font-mono-data text-muted-foreground self-center ml-1">{node.version}</span>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{node.description}</p>

        <div className="space-y-4">
          <div className="p-3 rounded border border-border bg-muted/20">
            <div className="text-[10px] font-mono-data uppercase tracking-widest text-muted-foreground mb-1">Layer Assignment</div>
            <div className="text-sm font-medium text-foreground">{node.layer}</div>
          </div>

          {parent && (
            <div>
              <div className="text-[10px] font-mono-data uppercase tracking-widest text-muted-foreground mb-2">Parent Element</div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground p-2 rounded border border-border bg-muted/10">
                <span className="text-primary/50">↑</span>
                {parent.label}
              </div>
            </div>
          )}

          <div>
            <div className="text-[10px] font-mono-data uppercase tracking-widest text-muted-foreground mb-2">Related Elements</div>
            <div className="flex flex-col gap-1.5">
              {related.map(rel => (
                <div key={`panel-rel-${rel.id}`} className="flex items-center gap-2 text-xs p-2 rounded border border-border bg-muted/10">
                  <span className="text-primary/50">↔</span>
                  <span className="text-foreground flex-1">{rel.label}</span>
                  <StatusBadge status={rel.status} size="xs" />
                </div>
              ))}
            </div>
          </div>

          <div className="p-3 rounded border border-border bg-muted/20 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono-data uppercase tracking-widest text-muted-foreground">Source / Authority</span>
              <AuthorityBadge authority={node.authority} size="xs" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono-data uppercase tracking-widest text-muted-foreground">Status</span>
              <StatusBadge status={node.status} size="xs" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono-data uppercase tracking-widest text-muted-foreground">Version</span>
              <span className="text-[11px] font-mono-data text-muted-foreground">{node.version}</span>
            </div>
          </div>

          <div className="pt-2 border-t border-border">
            <DemoBadge label="PROTOTYPE — Atlas node data" />
          </div>
        </div>
      </div>
    </div>
  );
}